import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [filesRes, logsRes] = await Promise.all([
          fetch('/api/files'),
          fetch(`/api/logs?limit=100${filterAction ? `&action=${filterAction}` : ''}${filterStart ? `&start=${filterStart}` : ''}${filterEnd ? `&end=${filterEnd}` : ''}`),
        ]);

        if (filesRes.ok) {
          const filesJson = await filesRes.json();
          setFiles(Array.isArray(filesJson.files) ? filesJson.files : []);
        }
        if (logsRes.ok) {
          const logsJson = await logsRes.json();
          const normalized = (Array.isArray(logsJson.logs) ? logsJson.logs : []).map((log) => ({
            ...log,
            file: log.file || log.file_name || log.file_id,
            ts: log.ts || log.created_at || log.timestamp,
            user: log.user || log.username,
          }));
          setLogs(normalized);
        }
      } catch (err) {
        console.error('Dashboard load failed', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filterAction, filterStart, filterEnd]);

  const uploads = logs.filter((l) => l.action === 'upload');
  const redactions = logs.filter((l) => l.action === 'redact');
  const downloads = logs.filter((l) => l.action === 'download');

  const latestFiles = files.slice(0, 6);
  const recentLogs = logs.slice(0, 8);

  const scrollToSection = (id) => {
    const node = document.getElementById(id);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="dashboard-shell">
      <div className="dashboard-layout">
        <aside className="dash-sidebar">
          <div className="dash-brand">
            <span className="dash-brand-mark">Redact</span>
            <span className="dash-brand-ring">Ø</span>
          </div>
          <div className="dash-sidebar-card">
            <p className="muted">Workspace overview</p>
            <div className="dash-sidebar-metric">
              <span>Files</span>
              <strong>{files.length}</strong>
            </div>
            <div className="dash-sidebar-metric">
              <span>Redactions</span>
              <strong>{redactions.length}</strong>
            </div>
            <div className="dash-sidebar-metric">
              <span>Downloads</span>
              <strong>{downloads.length}</strong>
            </div>
          </div>
          <nav className="dash-nav">
            <button className="dash-nav-btn primary" onClick={() => navigate('/app')}>
              Open redaction UI
            </button>
            <button className="dash-nav-btn" onClick={() => scrollToSection('dash-files')}>
              Recent files
            </button>
            <button className="dash-nav-btn" onClick={() => scrollToSection('dash-logs')}>
              Audit logs
            </button>
            <a className="dash-nav-link" href="/api/logs?limit=500&format=csv">
              Download CSV
            </a>
          </nav>
          <button className="dash-back" onClick={() => navigate('/app')}>
            <ArrowBackIosNewRoundedIcon fontSize="small" /> Back to workspace
          </button>
        </aside>

        <main className="dash-main">
          <header className="dash-top">
            <div className="dash-title">
              <div className="pill">Live</div>
              <div>
                <h1>Dashboard</h1>
                <p>Overview of your activity, files, and trust signals.</p>
              </div>
            </div>
            <div className="dash-top-actions">
              <button className="ghost" onClick={() => navigate('/app')}>
                Return to editor
              </button>
            </div>
          </header>

          <section className="dash-grid">
        <div className="dash-card highlight">
          <div className="card-top">
            <span className="label">Uploads</span>
            <UploadFileOutlinedIcon />
          </div>
          <h2>{uploads.length}</h2>
          <p>Uploads tracked in the last 100 events.</p>
        </div>
        <div className="dash-card">
          <div className="card-top">
            <span className="label">Redactions</span>
            <GppGoodOutlinedIcon />
          </div>
          <h2>{redactions.length}</h2>
          <p>Files redacted across your workspace.</p>
        </div>
        <div className="dash-card">
          <div className="card-top">
            <span className="label">Downloads</span>
            <CloudDownloadOutlinedIcon />
          </div>
          <h2>{downloads.length}</h2>
          <p>Exports delivered to you and collaborators.</p>
        </div>
        <div className="dash-card mini">
          <div className="card-top">
            <span className="label">Activity</span>
            <TimelineRoundedIcon />
          </div>
          <p className="mini-copy">Live audit log powered by the new /api/logs endpoint.</p>
        </div>
          </section>

          <section className="dash-panels">
            <div className="panel" id="dash-files">
          <div className="panel-head">
            <h3>Recent files</h3>
            <button className="link" onClick={() => navigate('/app')}>Open in app</button>
          </div>
          {latestFiles.length === 0 ? (
            <p className="muted">No files yet. Upload to see history.</p>
          ) : (
            <ul className="file-list">
              {latestFiles.map((file) => (
                <li key={file.id || file.title}>
                  <div>
                    <div className="file-name">{file.title || file.original_basename || 'Untitled.pdf'}</div>
                    <div className="muted">{file.pages || file.number_of_pages || '—'} pages</div>
                  </div>
                  <div className="muted ts">{new Date(file.created_at || file.uploaded_at || Date.now()).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
            </div>

            <div className="panel" id="dash-logs">
          <div className="panel-head">
            <h3>Audit log</h3>
            <div className="panel-actions">
              <span className="muted">Latest 100 events</span>
              <a className="link" href="/api/logs?limit=500&format=csv">Download CSV</a>
            </div>
          </div>
          <div className="log-filters">
            <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
              <option value="">All actions</option>
              <option value="upload">Upload</option>
              <option value="redact">Redact</option>
              <option value="download">Download</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
            </select>
            <input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} />
            <input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} />
            <button className="link" type="button" onClick={() => {setFilterAction(''); setFilterStart(''); setFilterEnd('');}}>Clear</button>
          </div>
          <div className="log-table">
            {recentLogs.length === 0 ? (
              <p className="muted">No activity recorded.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>File</th>
                    <th>User</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLogs.map((log) => (
                    <tr key={log.id || `${log.action}-${log.ts}`}>
                      <td className="log-action">{log.action}</td>
                      <td>{log.file || '—'}</td>
                      <td>{log.user || 'user'}</td>
                      <td>{new Date(log.ts || Date.now()).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
            </div>
          </section>

          {loading && <div className="dash-loading">Loading dashboard…</div>}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
